/**
 * SA 請求書 発行前プレビューダイアログ (v3.4)。
 *
 * 「請求可能」タブで選択した 報酬レコード + 個別説明代行料 を、
 * 実際の請求書と同じ明細形式で確認してから発行する。
 *  - 明細プレビュー (報酬行 + 「個別説明代行 × N 件」集約行 + 税抜合計)
 *  - 対象期間ラベル (既定: 当月「YYYY年M月分」) / メモ
 *  - 確定で createCommissionInvoice (briefingRecordIds 明示選択) を実行
 */
import { FileText, Loader2, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateCommissionInvoice,
  type UnbilledBriefingItem,
} from '@/hooks/useCommissionInvoices';
import type { CommissionRecordWithRef } from '@/hooks/useCommissionRecords';

function defaultPeriodLabel(): string {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月分`;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択済みの報酬レコード */
  records: CommissionRecordWithRef[];
  /** 選択済みの個別説明 */
  briefings: UnbilledBriefingItem[];
  /** 1 件あたり代行料 */
  briefingFee: number;
  /** 発行成功後 (発行済タブへの切替など) */
  onIssued?: () => void;
}

export function SaInvoiceIssueDialog({
  open,
  onOpenChange,
  records,
  briefings,
  briefingFee,
  onIssued,
}: Props) {
  const create = useCreateCommissionInvoice();
  const [periodLabel, setPeriodLabel] = useState(defaultPeriodLabel);
  const [memo, setMemo] = useState('');

  // ダイアログを開くたびに入力を初期化 (open が false → true の遷移時のみ)
  useEffect(() => {
    if (open) {
      setPeriodLabel(defaultPeriodLabel());
      setMemo('');
    }
  }, [open]);

  const recordTotal = records.reduce((s, r) => s + r.finalAmount, 0);
  const briefingTotal = briefings.length * briefingFee;
  const total = recordTotal + briefingTotal;

  const handleSubmit = async () => {
    try {
      const res = await create.mutateAsync({
        recordPaths: records.map((r) => r.path),
        briefingRecordIds: briefings.map((b) => ({
          dealId: b.dealId,
          recordId: b.recordId,
        })),
        periodLabel: periodLabel.trim() || null,
        memo: memo.trim() || null,
      });
      if (res.mailSent) {
        toast.success(`請求書 ${res.invoiceNumber} を発行しました (経理にメール送信完了)`);
      } else {
        toast.warning(
          `請求書 ${res.invoiceNumber} を発行しましたが、メール送信失敗: ${res.mailReason ?? '理由不明'}`,
        );
      }
      onOpenChange(false);
      onIssued?.();
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>請求書発行の確認</DialogTitle>
          <DialogDescription>
            以下の内容で請求書を作成し、経理担当宛にメール送信します。
          </DialogDescription>
        </DialogHeader>

        {/* 明細プレビュー */}
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="px-3 py-1.5 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            明細プレビュー
          </div>
          <div className="max-h-56 overflow-y-auto">
            {records.map((r) => (
              <div
                key={r.path}
                className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs border-t border-[var(--border)]"
              >
                <div className="min-w-0">
                  <span className="font-medium">{r.productName}</span>
                  <span className="text-[var(--text-muted)]"> — {r.dealTitle} ({r.contactName})</span>
                </div>
                <div className="tabular-nums font-medium shrink-0">
                  ¥{r.finalAmount.toLocaleString()}
                </div>
              </div>
            ))}
            {briefings.length > 0 && (
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs border-t border-[var(--border)] bg-[var(--accent-subtle)]">
                <div className="text-[var(--accent)]">
                  個別説明代行 × {briefings.length} 件 (@¥{briefingFee.toLocaleString()})
                </div>
                <div className="tabular-nums font-medium text-[var(--accent)] shrink-0">
                  ¥{briefingTotal.toLocaleString()}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between px-3 py-2 text-xs border-t border-[var(--border)] bg-[var(--bg-elevated)]">
            <div className="font-semibold">合計 (税抜)</div>
            <div className="tabular-nums font-semibold text-sm">¥{total.toLocaleString()}</div>
          </div>
        </div>
        <p className="mt-1.5 text-[10px] text-[var(--text-muted)]">
          消費税はインボイス登録の有無に応じて発行時に自動計算されます。
        </p>

        {/* 期間ラベル + メモ */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-[10px] text-[var(--text-muted)] mb-1">
              対象期間ラベル (任意)
            </label>
            <input
              type="text"
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
              placeholder="例: 2026年7月分"
              className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            />
          </div>
          <div>
            <label className="block text-[10px] text-[var(--text-muted)] mb-1">
              メモ (任意)
            </label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="経理への申し送り"
              className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-3 text-[11px] text-[var(--text-muted)]">
          <Mail className="size-3.5" />
          発行と同時に経理担当宛へ請求書メールが送信されます
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--border)]">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <FileText className="size-3.5" />
            )}
            請求書発行 + 経理にメール
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
